import React, { useState } from 'react'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import API from 'common/logic/API'
import { Button, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import { Fund, Tag } from 'types'
import TagSelect from 'common/components/TagSelect'
import { decodeFilterTree } from 'common/logic/FilterTree'

interface Props {
    query: Record<string, string>
    ids?: number[]
    onSuccess: (result: unknown) => void
}

function BulkEditMovementsWorkflow({ query, ids, onSuccess }: Props) {

    const loadingStep = (_parameters: unknown) =>
        Promise.all([
            API.get('/funds').then(r => (r.data as any).results as Fund[]),
            API.get('/tags').then(r => (r.data as any).results as Tag[])
        ]).then(([funds, tags]) => ({ funds, tags }))

    const userInputStep = (
        _parameters: unknown,
        loading_result: unknown,
        _values: unknown,
        _errors: unknown,
        completion: (input: unknown) => void
    ) => {
        const { funds, tags } = loading_result as { funds: Fund[]; tags: Tag[] }
        return {
            component: <BulkEditForm funds={funds} tags={tags} completion={completion} />,
            title: 'Edit movements in bulk'
        }
    }

    const executionStep = (_parameters: unknown, _loading_result: unknown, user_input: unknown) => {
        const { fundId, updateDescription, description, addTagIds, removeTagIds } = user_input as any

        const updates: Record<string, unknown> = {}
        if (fundId !== null) updates.fundId = fundId
        if (updateDescription) {
            updates.updateDescription = true
            updates.description = description
        }
        if (addTagIds && addTagIds.length > 0) updates.addTagIds = addTagIds
        if (removeTagIds && removeTagIds.length > 0) updates.removeTagIds = removeTagIds

        const filters: Record<string, unknown> = {}
        if (ids && ids.length > 0) {
            filters.ids = ids
        } else if (query.filter) {
            const parsedTree = decodeFilterTree(query.filter)
            if (!parsedTree) return Promise.reject(new Error('Could not parse the active filter. Please clear the filter and try again.'))
            filters.filterTree = parsedTree
        }

        return API.put('/movements/bulk', { filters, updates })
    }

    const completionCallback = (result: unknown) => {
        onSuccess(result && (result as any).data)
    }

    return (
        <OperationWorkflowModal
            loadingStep={loadingStep}
            userInputStep={userInputStep}
            executionStep={executionStep}
            completionCallback={completionCallback}
        />
    )
}

interface FormProps {
    funds: Fund[]
    tags: Tag[]
    completion: (input: unknown) => void
}

function BulkEditForm({ funds, tags: initialTags, completion }: FormProps) {
    const activeFunds = funds.filter(f => !f.deleted)

    const [availableTags, setAvailableTags] = useState<Tag[]>(initialTags)
    const [addTagIds, setAddTagIds] = useState<number[]>([])
    const [removeTagIds, setRemoveTagIds] = useState<number[]>([])

    const initialValues = {
        editFund: false,
        fundId: activeFunds[0]?.id ?? '',
        editDescription: false,
        description: '',
        editAddTags: false,
        editRemoveTags: false,
    }

    const handleSubmit = (values: typeof initialValues) => {
        completion({
            fundId: values.editFund ? values.fundId : null,
            updateDescription: values.editDescription,
            description: values.editDescription ? values.description : null,
            addTagIds: values.editAddTags ? addTagIds : [],
            removeTagIds: values.editRemoveTags ? removeTagIds : [],
        })
    }

    return (
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
            {({ handleSubmit, handleChange, values }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="editFund"
                            name="editFund"
                            label="Set fund"
                            checked={values.editFund}
                            onChange={handleChange}
                        />
                        <Form.Control
                            as="select"
                            name="fundId"
                            value={values.fundId}
                            onChange={handleChange}
                            disabled={!values.editFund}
                        >
                            {activeFunds.map(f => <option value={f.id} key={f.id}>{f.name}</option>)}
                        </Form.Control>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="editDescription"
                            name="editDescription"
                            label="Set description"
                            checked={values.editDescription}
                            onChange={handleChange}
                        />
                        <Form.Control
                            type="text"
                            name="description"
                            placeholder="Description"
                            value={values.description}
                            onChange={handleChange}
                            disabled={!values.editDescription}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="editAddTags"
                            name="editAddTags"
                            label="Add tags"
                            checked={values.editAddTags}
                            onChange={handleChange}
                        />
                        <TagSelect
                            selectedTagIds={addTagIds}
                            availableTags={availableTags}
                            isDisabled={!values.editAddTags}
                            onChange={(ids, updatedTags) => {
                                setAddTagIds(ids)
                                setAvailableTags(updatedTags)
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="editRemoveTags"
                            name="editRemoveTags"
                            label="Remove tags"
                            checked={values.editRemoveTags}
                            onChange={handleChange}
                        />
                        <TagSelect
                            selectedTagIds={removeTagIds}
                            availableTags={availableTags}
                            isDisabled={!values.editRemoveTags}
                            onChange={(ids, updatedTags) => {
                                setRemoveTagIds(ids)
                                setAvailableTags(updatedTags)
                            }}
                        />
                    </Form.Group>

                    <Button variant="primary" type="submit">Apply to all matching movements</Button>
                </Form>
            )}
        </Formik>
    )
}

export default BulkEditMovementsWorkflow
